import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import './ReturnInventoryPicker.css';

/** One SKU the customer still holds, per GET /api/customers/<tag>/outstanding. */
export interface OutstandingItem {
  sku_code: string;
  product_name: string;
  active: number;
}

export interface ReturnPackage {
  id: number;
  name: string;
  items: { sku_code: string; quantity: number }[];
}

/** What the picker reads and writes: one entry per product line. */
export interface ReturnLine {
  product_name: string;
  quantity: number;
}

interface Props {
  customerTag: string;
  packages: ReturnPackage[];
  /** sku_code -> product_name as the product dropdown knows it, so a line the
   *  picker writes is one the dropdown can display. */
  productNameForSku: (skuCode: string) => string | undefined;
  lines: ReturnLine[];
  onChange: (lines: ReturnLine[]) => void;
}

/** Complete sets of `pkg` the customer holds, given what is outstanding. */
export const setsHeld = (pkg: ReturnPackage, activeBySku: Map<string, number>): number =>
  pkg.items.length === 0
    ? 0
    : Math.min(...pkg.items.map(i => Math.floor((activeBySku.get(i.sku_code) || 0) / i.quantity)));

/**
 * Return-label helper: lists what the customer still has out so the rep picks
 * from it instead of guessing. The product lines stay the single source of
 * truth — this only reads and rewrites them — so a line added by hand below
 * and a line ticked here are the same thing.
 */
const ReturnInventoryPicker: React.FC<Props> = ({
  customerTag, packages, productNameForSku, lines, onChange,
}) => {
  const [outstanding, setOutstanding] = useState<OutstandingItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setOutstanding(null);
    setLoadError(null);
    if (!customerTag) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/customers/${encodeURIComponent(customerTag)}/outstanding`)
      .then(async r => {
        const body = await r.json().catch(() => null);
        if (!r.ok || !body || !Array.isArray(body.outstanding)) {
          throw new Error(body?.error || 'Could not load customer inventory');
        }
        return body.outstanding as OutstandingItem[];
      })
      .then(items => { if (!cancelled) setOutstanding(items.filter(i => i.active > 0)); })
      .catch(err => { if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err)); });
    return () => { cancelled = true; };
  }, [customerTag]);

  const nameFor = (item: { sku_code: string; product_name?: string }) =>
    productNameForSku(item.sku_code) || item.product_name || item.sku_code;

  const qtyOf = (productName: string) =>
    lines.find(l => l.product_name === productName)?.quantity || 0;

  /** Set one product's quantity (0 removes it), keeping every other line. */
  const withQuantity = (current: ReturnLine[], productName: string, quantity: number): ReturnLine[] => {
    const kept = current.filter(l => l.product_name && l.product_name !== productName);
    if (quantity <= 0) return kept;
    const existing = current.findIndex(l => l.product_name === productName);
    const line = { product_name: productName, quantity };
    if (existing === -1) return [...kept, line];
    // Preserve the line's position so rows don't jump around while editing.
    const next = current.filter(l => l.product_name);
    next[next.findIndex(l => l.product_name === productName)] = line;
    return next;
  };

  if (loadError) {
    return (
      <div className="return-picker return-picker-error" role="alert">
        Couldn&rsquo;t load this customer&rsquo;s inventory ({loadError}). Add return items manually below.
      </div>
    );
  }
  if (!outstanding) {
    return <div className="return-picker return-picker-loading">Loading customer inventory…</div>;
  }
  if (outstanding.length === 0) {
    return (
      <div className="return-picker return-picker-empty">
        No equipment on record with this customer. Add return items manually below if needed.
      </div>
    );
  }

  const activeBySku = new Map(outstanding.map(i => [i.sku_code, i.active]));
  const selectedCount = outstanding.filter(i => qtyOf(nameFor(i)) > 0).length;

  const returnPackage = (pkg: ReturnPackage) => {
    let next = lines;
    for (const item of pkg.items) {
      const name = nameFor(item);
      const cap = activeBySku.get(item.sku_code) || 0;
      next = withQuantity(next, name, Math.min(cap, qtyOf(name) + item.quantity));
    }
    onChange(next);
  };

  const selectAll = () => {
    let next = lines;
    for (const item of outstanding) next = withQuantity(next, nameFor(item), item.active);
    onChange(next);
  };

  const clearAll = () => {
    const names = new Set(outstanding.map(nameFor));
    onChange(lines.filter(l => l.product_name && !names.has(l.product_name)));
  };

  return (
    <div className="return-picker">
      <div className="return-picker-header">
        <span className="return-picker-title">Customer&rsquo;s equipment</span>
        <span className="return-picker-actions">
          <button type="button" className="return-picker-link" onClick={selectAll}>Return everything</button>
          {selectedCount > 0 && (
            <button type="button" className="return-picker-link" onClick={clearAll}>Clear</button>
          )}
        </span>
      </div>

      {packages.length > 0 && (
        <div className="return-picker-packages">
          {packages.map(pkg => {
            const sets = setsHeld(pkg, activeBySku);
            return (
              <button
                key={pkg.id}
                type="button"
                className="return-picker-package"
                disabled={sets === 0}
                onClick={() => returnPackage(pkg)}
                title={sets === 0
                  ? 'This customer does not have a complete set of this package on record'
                  : 'Adds one full set to the return'}
              >
                + {pkg.name}
                <span className="return-picker-package-sets">
                  {sets === 0 ? 'not held' : `${sets} set${sets === 1 ? '' : 's'} out`}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <ul className="return-picker-items">
        {outstanding.map(item => {
          const name = nameFor(item);
          const qty = qtyOf(name);
          const checked = qty > 0;
          const id = `return-item-${item.sku_code}`;
          return (
            <li key={item.sku_code} className={`return-picker-item${checked ? ' selected' : ''}`}>
              <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={e => onChange(withQuantity(lines, name, e.target.checked ? item.active : 0))}
              />
              <label htmlFor={id} className="return-picker-item-name">{name}</label>
              <span className="return-picker-item-out">{item.active} out</span>
              <input
                type="number"
                aria-label={`Return quantity for ${name}`}
                className="return-picker-item-qty"
                min={0}
                max={item.active}
                value={qty || ''}
                placeholder="0"
                onChange={e => {
                  const raw = parseInt(e.target.value, 10);
                  const val = Number.isNaN(raw) ? 0 : Math.max(0, Math.min(item.active, raw));
                  onChange(withQuantity(lines, name, val));
                }}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ReturnInventoryPicker;
